'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { createPortal } from 'react-dom';
import { useEffect, useMemo, useState } from 'react';
import { X } from 'phosphor-react';
import { useToast, type ToastItem, type ToastVariant } from '@/contexts/ToastContext';

function getAccentBar(variant: ToastVariant): string {
  switch (variant) {
    case 'success':
      return 'bg-[#6927da]';
    case 'error':
      return 'bg-[#e11d48]';
    case 'info':
      return 'bg-[#2563eb]';
    default:
      return 'bg-[#6b7280]';
  }
}

function ToastCard({
  toast,
  onDismiss,
}: {
  toast: ToastItem;
  onDismiss: () => void;
}) {
  const durationSec = Math.max(1.2, Math.min((toast.durationMs || 2500) / 1000, 10));
  const hasDescription = Boolean(toast.description?.trim());
  const showTitle = Boolean(toast.title?.trim() && hasDescription);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -20, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -12, scale: 0.97 }}
      transition={{ type: 'spring', stiffness: 380, damping: 28 }}
      className="pointer-events-auto w-full"
    >
      <div
        className={[
          'relative overflow-hidden rounded-xl border',
          'bg-white border-[#e5e7eb]',
          'shadow-[0_10px_40px_-12px_rgba(15,23,42,0.2)]',
          'dark:bg-[#1c1c1f] dark:border-[#3f3f46]',
          'dark:shadow-[0_12px_40px_-10px_rgba(0,0,0,0.55)]',
        ].join(' ')}
        role="status"
        aria-live="polite"
      >
        <motion.div
          className={['absolute left-0 top-0 h-[3px] w-full origin-left', getAccentBar(toast.variant)].join(' ')}
          initial={{ scaleX: 1 }}
          animate={{ scaleX: 0 }}
          transition={{ duration: durationSec, ease: 'linear' }}
        />

        <div className="flex items-start gap-3 px-4 py-3.5">
          <div className="min-w-0 flex-1 pt-0.5 text-left">
            {showTitle && (
              <p className="text-sm font-semibold leading-snug text-[#111827] dark:text-[#f9fafb]">
                {toast.title}
              </p>
            )}
            {(hasDescription || toast.title) && (
              <p
                className={[
                  'text-sm leading-relaxed',
                  showTitle
                    ? 'mt-1 text-[#4b5563] dark:text-[#d1d5db]'
                    : 'font-medium text-[#111827] dark:text-[#f9fafb]',
                ].join(' ')}
              >
                {hasDescription ? toast.description : toast.title}
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={onDismiss}
            className={[
              'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg',
              'text-[#6b7280] hover:bg-[#f3f4f6] hover:text-[#111827]',
              'dark:text-[#a1a1aa] dark:hover:bg-[#27272a] dark:hover:text-[#f9fafb]',
              'transition-colors',
            ].join(' ')}
            aria-label="Fechar notificação"
          >
            <X className="h-4 w-4" weight="bold" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export function Toaster() {
  const { toasts, dismiss } = useToast();
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  const portalTarget = useMemo(() => {
    if (typeof document === 'undefined') return null;
    return document.body;
  }, []);

  if (!mounted || !portalTarget) return null;

  return createPortal(
    <div
      className="fixed top-5 left-1/2 z-[9999] flex w-full max-w-[min(420px,calc(100vw-2rem))] -translate-x-1/2 flex-col items-stretch gap-2.5 pointer-events-none"
      aria-label="Notificações"
    >
      <AnimatePresence mode="popLayout" initial={false}>
        {toasts.map((t) => (
          <ToastCard key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </AnimatePresence>
    </div>,
    portalTarget,
  );
}
